import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FiPhone, FiSettings, FiTrash2, FiEdit2, FiCheck, FiBarChart2,
  FiMessageSquare, FiMic, FiMapPin, FiClock
} from 'react-icons/fi';
import { BsToggle2Off, BsToggle2On } from 'react-icons/bs';
import toast from 'react-hot-toast';
import { api } from '../../utils/api';

interface PhoneNumber {
  id: string;
  number: string;
  friendlyName: string;
  type: string;
  status: string;
  capabilities: string[];
  provider: string;
  recordCalls: boolean;
  transcribeCalls: boolean;
  channels?: Array<{
    id: string;
    type: string;
    assistant?: {
      id: string;
      name: string;
    };
  }>;
  _count?: {
    calls: number;
    smsMessages: number;
  };
}

interface NumberManagementPanelProps {
  organizationId: string;
  onRefresh?: () => void;
}

export const NumberManagementPanel: React.FC<NumberManagementPanelProps> = ({
  organizationId,
  onRefresh
}) => {
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  useEffect(() => {
    loadPhoneNumbers();
  }, [organizationId]);

  const loadPhoneNumbers = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/organizations/${organizationId}/phone-numbers`);
      setPhoneNumbers(response.data.phoneNumbers || []);
    } catch (error) {
      console.error('Failed to load phone numbers:', error);
      toast.error('Failed to load phone numbers');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateName = async (numberId: string) => {
    try {
      await api.put(`/organizations/${organizationId}/phone-numbers/${numberId}`, {
        friendlyName: editName
      });

      toast.success('Name updated successfully');
      setEditingId(null);
      loadPhoneNumbers();
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to update name');
    }
  };

  const handleToggleSetting = async (
    numberId: string,
    setting: 'recordCalls' | 'transcribeCalls',
    currentValue: boolean
  ) => {
    try {
      await api.put(`/organizations/${organizationId}/phone-numbers/${numberId}`, {
        [setting]: !currentValue
      });

      toast.success(`${setting === 'recordCalls' ? 'Recording' : 'Transcription'} ${!currentValue ? 'enabled' : 'disabled'}`);
      loadPhoneNumbers();
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to update setting');
    }
  };

  const handleDeleteNumber = async (numberId: string, number: string) => {
    if (!confirm(`Are you sure you want to release ${number}? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/organizations/${organizationId}/phone-numbers/${numberId}`);
      toast.success('Phone number released successfully');
      loadPhoneNumbers();
      onRefresh?.();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to release number');
    }
  };

  const startEditing = (phoneNumber: PhoneNumber) => {
    setEditingId(phoneNumber.id);
    setEditName(phoneNumber.friendlyName);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditName('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (phoneNumbers.length === 0) {
    return (
      <div className="text-center py-12 bg-slate-800/50 rounded-xl border border-slate-700">
        <FiPhone className="mx-auto text-gray-600 text-5xl mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">No Phone Numbers</h3>
        <p className="text-gray-400">
          Search and provision numbers to get started
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <FiSettings className="text-blue-400" />
          Your Phone Numbers ({phoneNumbers.length})
        </h3>
        <button
          onClick={loadPhoneNumbers}
          className="px-3 py-1 text-sm text-blue-400 hover:text-blue-300 transition-colors"
        >
          Refresh
        </button>
      </div>

      {phoneNumbers.map((phoneNumber, index) => (
        <motion.div
          key={phoneNumber.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
          className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700 hover:border-slate-600 transition-all"
        >
          {/* Header Row */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <FiPhone className="text-blue-400" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-white font-semibold text-lg">{phoneNumber.number}</p>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      phoneNumber.status === 'ACTIVE'
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-gray-500/20 text-gray-400'
                    }`}>
                      {phoneNumber.status}
                    </span>
                  </div>

                  {editingId === phoneNumber.id ? (
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="px-2 py-1 bg-slate-900 border border-slate-700 rounded text-white text-sm"
                        autoFocus
                      />
                      <button
                        onClick={() => handleUpdateName(phoneNumber.id)}
                        className="text-green-400 hover:text-green-300"
                      >
                        <FiCheck />
                      </button>
                      <button
                        onClick={cancelEditing}
                        className="text-red-400 hover:text-red-300"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-gray-400 text-sm">{phoneNumber.friendlyName}</p>
                      <button
                        onClick={() => startEditing(phoneNumber)}
                        className="text-gray-500 hover:text-gray-300"
                      >
                        <FiEdit2 className="text-xs" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Capabilities */}
              <div className="flex items-center gap-2 ml-13">
                {phoneNumber.capabilities.map((cap) => (
                  <span
                    key={cap}
                    className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded-full"
                  >
                    {cap.toUpperCase()}
                  </span>
                ))}
                <span className="text-xs bg-slate-700 text-gray-400 px-2 py-1 rounded-full">
                  {phoneNumber.provider}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleDeleteNumber(phoneNumber.id, phoneNumber.number)}
                className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-all"
                title="Release number"
              >
                <FiTrash2 />
              </button>
            </div>
          </div>

          {/* Assistant Assignment */}
          {phoneNumber.channels && phoneNumber.channels.length > 0 && (
            <div className="mb-4 p-3 bg-slate-900/50 rounded-lg border border-slate-700">
              <p className="text-sm text-gray-400 mb-1">Assigned Assistant:</p>
              <p className="text-white font-medium">
                {phoneNumber.channels[0].assistant?.name || 'No assistant'}
              </p>
            </div>
          )}

          {/* Settings Toggles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Call Recording */}
            <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg border border-slate-700">
              <div className="flex items-center gap-2">
                <FiMic className="text-orange-400" />
                <div>
                  <p className="text-white text-sm font-medium">Call Recording</p>
                  <p className="text-gray-500 text-xs">Record all calls</p>
                </div>
              </div>
              <button
                onClick={() => handleToggleSetting(phoneNumber.id, 'recordCalls', phoneNumber.recordCalls)}
                className="text-2xl"
              >
                {phoneNumber.recordCalls ? (
                  <BsToggle2On className="text-green-400" />
                ) : (
                  <BsToggle2Off className="text-gray-500" />
                )}
              </button>
            </div>

            {/* Transcription */}
            <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg border border-slate-700">
              <div className="flex items-center gap-2">
                <FiMessageSquare className="text-blue-400" />
                <div>
                  <p className="text-white text-sm font-medium">Transcription</p>
                  <p className="text-gray-500 text-xs">Auto-transcribe calls</p>
                </div>
              </div>
              <button
                onClick={() => handleToggleSetting(phoneNumber.id, 'transcribeCalls', phoneNumber.transcribeCalls)}
                className="text-2xl"
              >
                {phoneNumber.transcribeCalls ? (
                  <BsToggle2On className="text-green-400" />
                ) : (
                  <BsToggle2Off className="text-gray-500" />
                )}
              </button>
            </div>
          </div>

          {/* Stats */}
          {phoneNumber._count && (
            <div className="mt-4 pt-4 border-t border-slate-700">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <FiPhone className="text-green-400" />
                  <div>
                    <p className="text-white font-semibold">{phoneNumber._count.calls || 0}</p>
                    <p className="text-gray-400 text-xs">Total Calls</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <FiMessageSquare className="text-purple-400" />
                  <div>
                    <p className="text-white font-semibold">{phoneNumber._count.smsMessages || 0}</p>
                    <p className="text-gray-400 text-xs">SMS Messages</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      ))}
    </div>
  );
};

